/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { ReduxUtils } from '@spm/core';

import {
  POST_ACES_CHAP_WAITLIST_ENROLL,
  FETCH_ACES_CHAP_WAITLIST_APPLIED,
  FETCH_ACES_CHAP_WAITLIST_FUND_AVAILABLE,
  POST_ACES_CHAP_WAITLIST_SUBMIT_FORM,
} from './actionTypes';

import {
  postAcesChapWaitlistEnrollUtil,
  fetchAcesChapWaitlistAppliedUtil,
  fetchAcesChapWaitlistFundAvailableUtil,
  postAcesChapWaitlistSubmitFormUtil,
} from './utils';

/**
 * Contains the action creator functions that update the ACESWaitlist slice of the store.
 *
 * @namespace ACESWaitlistActions
 */
export default class actions {
  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_chap_waitlist_enroll
   *
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof ACESWaitlistActions
   */
  static postAcesChapWaitlistEnroll = (dispatch, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postAcesChapWaitlistEnrollUtil.bind(null, {}),
      POST_ACES_CHAP_WAITLIST_ENROLL,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postAcesChapWaitlistEnroll.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESWaitlistActions
   */
  static resetPostAcesChapWaitlistEnroll = (dispatch, data) => {
    const json = {
      type: POST_ACES_CHAP_WAITLIST_ENROLL.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postAcesChapWaitlistEnroll.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESWaitlistActions
   */
  static resetPostAcesChapWaitlistEnrollError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_ACES_CHAP_WAITLIST_ENROLL.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_chap_waitlist_applied
   *
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof ACESWaitlistActions
   */
  static fetchAcesChapWaitlistApplied = (dispatch, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      fetchAcesChapWaitlistAppliedUtil,
      FETCH_ACES_CHAP_WAITLIST_APPLIED,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchAcesChapWaitlistApplied.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESWaitlistActions
   */
  static resetFetchAcesChapWaitlistApplied = (dispatch, data) => {
    const json = {
      type: FETCH_ACES_CHAP_WAITLIST_APPLIED.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchAcesChapWaitlistApplied.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESWaitlistActions
   */
  static resetFetchAcesChapWaitlistAppliedError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_ACES_CHAP_WAITLIST_APPLIED.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_chap_waitlist_fund_available
   *
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof ACESWaitlistActions
   */
  static fetchAcesChapWaitlistFundAvailable = (dispatch, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      fetchAcesChapWaitlistFundAvailableUtil,
      FETCH_ACES_CHAP_WAITLIST_FUND_AVAILABLE,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchAcesChapWaitlistFundAvailable.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESWaitlistActions
   */
  static resetFetchAcesChapWaitlistFundAvailable = (dispatch, data) => {
    const json = {
      type: FETCH_ACES_CHAP_WAITLIST_FUND_AVAILABLE.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchAcesChapWaitlistFundAvailable.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESWaitlistActions
   */
  static resetFetchAcesChapWaitlistFundAvailableError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_ACES_CHAP_WAITLIST_FUND_AVAILABLE.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_chap_waitlist_submit_form
   *
   * @param {NewACESWaitlistKey} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof ACESWaitlistActions
   */
  static postAcesChapWaitlistSubmitForm = (dispatch, newACESWaitlistKey, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postAcesChapWaitlistSubmitFormUtil.bind(null, newACESWaitlistKey),
      POST_ACES_CHAP_WAITLIST_SUBMIT_FORM,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postAcesChapWaitlistSubmitForm.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESWaitlistActions
   */
  static resetPostAcesChapWaitlistSubmitForm = (dispatch, data) => {
    const json = {
      type: POST_ACES_CHAP_WAITLIST_SUBMIT_FORM.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postAcesChapWaitlistSubmitForm.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESWaitlistActions
   */
  static resetPostAcesChapWaitlistSubmitFormError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_ACES_CHAP_WAITLIST_SUBMIT_FORM.failureAction);
  };
}
